import type { SerializedCustomer } from '@enmon/commons/lib/enmon';
import type { SerializedUser } from '@enmon/commons/lib/enmon/users/user';
import { captureException } from '@sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useServices } from '@/components/Services';
import { authActions } from '@/containers/App/actions';
import { selectActiveUser } from '@/containers/App/selectors';
import {
  clearIdentifiedUser,
  clearPasswordResetToken,
  getStoredIdentifiedUser,
  type StoredIdentifiedUser,
  storeIdentifiedUser,
  storePasswordResetToken,
} from '@/routes/auth/utils/identifiedUserStorage';
import { authClient } from '@/services/auth-client';
import { MUTATION_KEYS } from '@/utils/mutationKeys';
import type { PopulatedUser } from '../../users';
import type {
  GenerateOtpArg,
  IdentifyArg,
  LoginArg,
  MissingRecoveryCodeArg,
  ResetPasswordArg,
  SetPasswordArg,
  VerifyOtpArg,
} from '../types/args';
import type { ApproveOAuthConsentResult } from '../types/results';
import { config } from '@/config';
import { useLocaleState } from '@/containers/LanguageProvider/hooks/useLocaleState';

const getRedirectPath = (
  defaultHomePage: SerializedUser['defaultHomePage'],
  lastAttemptedPath: string | null,
): string => {
  if (lastAttemptedPath) {
    return lastAttemptedPath;
  }

  if (defaultHomePage) {
    return `/${defaultHomePage}`;
  }
  return '/dashboard';
};

/**
 * If the current URL indicates an OAuth Authorization Code flow (`response_type=code`),
 * redirects to the backend authorization endpoint and returns `true`.
 */
const redirectIfOAuthCodeFlow = (search: string): boolean => {
  const queryParams = new URLSearchParams(search);
  if (queryParams.get('response_type') !== 'code') return false;

  const baseUrl = config.apiBaseUrl && config.apiBaseUrl !== '/' ? config.apiBaseUrl : window.location.origin;
  const authorizationUrl = new URL('/auth/v2/oauth2/authorize', baseUrl);
  authorizationUrl.search = queryParams.toString();
  window.location.href = authorizationUrl.toString();
  return true;
};

type EnableTwoFactorArg = Parameters<typeof authClient.twoFactor.enable>[0];
export type EnableTwoFactorData = NonNullable<Awaited<ReturnType<typeof authClient.twoFactor.enable>>['data']>;
type DisableTwoFactorArg = Parameters<typeof authClient.twoFactor.disable>[0];
export type DisableTwoFactorData = Awaited<ReturnType<typeof authClient.twoFactor.disable>>['data'];

export interface AuthenticateShape {
  identify: (arg: IdentifyArg) => void;
  verifyOtp: (arg: VerifyOtpArg) => void;
  generateOtp: (arg: GenerateOtpArg) => void;
  login: (arg: LoginArg) => void;
  loginToCustomer: (customerId: SerializedCustomer['_id']) => void;
  logoutFromCustomer: () => void;
  logout: () => void;
  resetPassword: (arg: ResetPasswordArg) => void;
  setPassword: (arg: SetPasswordArg) => void;
  missingRecoveryCodeAsync: (arg: MissingRecoveryCodeArg) => Promise<void>;
  verifyTotp: (arg: Parameters<typeof authClient.twoFactor.verifyTotp>[0]) => void;
  verifyTotpAsync: (arg: Parameters<typeof authClient.twoFactor.verifyTotp>[0]) => Promise<void>;
  verifyBackupCodeAsync: (arg: Parameters<typeof authClient.twoFactor.verifyBackupCode>[0]) => Promise<void>;
  enableTwoFactorAsync: (arg: EnableTwoFactorArg) => Promise<EnableTwoFactorData>;
  disableTwoFactorAsync: (arg: DisableTwoFactorArg) => Promise<DisableTwoFactorData>;
  approveOAuthConsentAsync: (oauthQuery: string) => Promise<ApproveOAuthConsentResult | null>;

  identifiedUser: StoredIdentifiedUser;
  loadSession: (password?: string, shouldRedirect?: boolean) => Promise<void>;

  resetMutation: {
    identify: () => void;
    generateOtp: () => void;
    verifyOtp: () => void;
    login: () => void;
    loginToCustomer: () => void;
    logoutFromCustomer: () => void;
    logout: () => void;
    resetPassword: () => void;
    setPassword: () => void;
    missingRecoveryCode: () => void;
    verifyTotp: () => void;
    verifyBackupCode: () => void;
    enableTwoFactor: () => void;
    disableTwoFactor: () => void;
    oauthConsent: () => void;
    all: () => void;
  };

  isLoading: boolean;
  isAuthenticated: boolean;
  loading: {
    isLoadingIdentify: boolean;
    isLoadingGenerateOtp: boolean;
    isLoadingVerifyOtp: boolean;
    isLoadingLogin: boolean;
    isLoadingLoginToCustomer: boolean;
    isLoadingLogoutFromCustomer: boolean;
    isLoadingLogout: boolean;
    isLoadingResetPassword: boolean;
    isLoadingSetPassword: boolean;
    isLoadingMissingRecoveryCode: boolean;
    isLoadingRestoreSession: boolean;
    isLoadingVerifyTotp: boolean;
    isLoadingVerifyBackupCode: boolean;
    isLoadingEnableTwoFactor: boolean;
    isLoadingDisableTwoFactor: boolean;
    isLoadingOAuthConsent: boolean;
  };

  isError: boolean;
  error: {
    identify?: Error | unknown | null;
    generateOtp?: Error | unknown | null;
    verifyOtp?: Error | unknown | null;
    login?: Error | unknown | null;
    loginToCustomer?: Error | unknown | null;
    logoutFromCustomer?: Error | unknown | null;
    logout?: Error | unknown | null;
    resetPassword?: Error | unknown | null;
    setPassword?: Error | unknown | null;
    missingRecoveryCode?: Error | unknown | null;
    restoreSession?: Error | unknown | null;
    verifyTotp?: Error | unknown | null;
    verifyBackupCode?: Error | unknown | null;
    enableTwoFactor?: Error | unknown | null;
    disableTwoFactor?: Error | unknown | null;
    oauthConsent?: Error | unknown | null;
  };
}

export const useAuthenticate = (): AuthenticateShape => {
  const history = useHistory();
  const location = useLocation();
  const { locale, setLocale } = useLocaleState();
  const dispatch = useDispatch();

  // Version tracker to trigger re-computation of identifiedUser from sessionStorage
  const [identifiedUserVersion, setIdentifiedUserVersion] = useState(0);

  // Compute identifiedUser from sessionStorage
  // biome-ignore lint: the use of identifiedUserVersion is intentional to trigger re-computation when it changes
  const identifiedUser = useMemo(() => getStoredIdentifiedUser(), [identifiedUserVersion]);

  const user = useSelector(selectActiveUser) satisfies PopulatedUser | undefined;

  const { authApiClient, usersApiClient, customersApiClient } = useServices();

  /**
   * Fetches session, user, and customer data after successful authentication or on session change.
   * Dispatches all necessary Redux actions and redirects to the appropriate page.
   */
  const loadSession = useCallback(
    async (password?: string, shouldRedirect?: boolean) => {
      // Fetch session data to determine activeCustomerId
      const sessionData = await authClient.getSession();

      // Session expired or invalid
      if (!sessionData.data) return;

      const { session } = sessionData.data;

      const { customerRequiresTwoFactor, twoFactorEnabled } = session;
      // If user's required to setup 2fa redirect to 2fa setup page
      if (customerRequiresTwoFactor && !twoFactorEnabled) {
        history.push({ pathname: '/two-factor', search: location.search }, { requireSetup: true, password });
        return;
      }

      if (location.pathname !== '/consent' && redirectIfOAuthCodeFlow(location.search)) return;

      // Fetch user data
      const userData = await usersApiClient.getMe(undefined);
      if (!userData) {
        throw new Error('User data not found');
      }

      // Change locale if needed
      if (userData.locale && userData.locale !== locale) {
        setLocale(userData.locale);
      }

      let customerData = null;

      // Fetch customer data if session has activeCustomerId, otherwise dispatch successful logout from customer
      if (sessionData?.data?.session?.activeCustomerId) {
        customerData = await customersApiClient.getMe(undefined);
      }

      // Dispatch successful session load to update redux state with user and customer data
      dispatch(authActions.loadSession.success({ user: userData, customerData }));

      if (shouldRedirect) {
        const lastAttemptedPath = sessionStorage.getItem('lastAttemptedPath');

        const redirectPath = getRedirectPath(userData.defaultHomePage, lastAttemptedPath);

        if (sessionData?.data?.session?.activeCustomerId) {
          sessionStorage.removeItem('lastAttemptedPath');
        }

        history.push(redirectPath);
      }
    },
    [usersApiClient, customersApiClient, dispatch, locale, history, location.search, location.pathname],
  );

  const {
    mutate: generateOtp,
    isLoading: isLoadingGenerateOtp,
    isError: isGenerateOtpError,
    error: errorGenerateOtp,
    reset: resetGenerateOtp,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.generateOtp(),
    mutationFn: async (arg: GenerateOtpArg) => await authApiClient.generateOtp(arg),
    onError: error => {
      console.error('Generate OTP error:', error);
      captureException(error);
    },
  });

  const {
    mutate: verifyOtp,
    isLoading: isLoadingVerifyOtp,
    isError: isVerifyOtpError,
    error: errorVerifyOtp,
    reset: resetVerifyOtp,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.verifyOtp(),
    mutationFn: async (arg: VerifyOtpArg) => await authApiClient.verifyOtp(arg),
    onSuccess: data => {
      if (!data?.passwordResetToken) {
        return;
      }
      // OTP flow always requires password change
      // Store the password reset token for use on set-password page
      storePasswordResetToken(data.passwordResetToken);
      dispatch(authActions.otp.success());
      history.push({ pathname: '/token/set-password', search: location.search });
    },
    onError: error => {
      // Error intentionally not dispatched, form is displaying error section
      console.error('Verify OTP error:', error);
      captureException(error);
    },
  });

  const {
    mutate: identify,
    isLoading: isLoadingIdentify,
    isError: isIdentifyError,
    error: errorIdentify,
    reset: resetIdentify,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.identify(),
    mutationFn: async (payload: IdentifyArg) => await authApiClient.identify(payload),
    onSuccess: data => {
      if (!data) {
        return;
      }

      // Store identifier and methods in session storage to persist across page refreshes
      if (data.identifier && data.methods) {
        storeIdentifiedUser(data);

        // Trigger re-computation of identifiedUser from sessionStorage
        setIdentifiedUserVersion(v => v + 1);
      }

      const { oneTimePassword } = data.methods;

      // If one-time password is available prioritize it over email-password flow
      // otherwise redirect to email login regardless of whether email-password method is actually available, to avoid dead-end for users with only one-time password method. The case of no methods available is handled by the previous check.
      if (oneTimePassword) {
        history.push({ pathname: '/one-time-password', search: location.search });
        return;
      }

      history.push({ pathname: '/login', search: location.search });
    },
    onError: error => {
      captureException(error);
    },
  });

  const {
    mutate: restoreSession,
    isLoading: isLoadingRestoreSession,
    error: errorRestoreSession,
    isError: isRestoreSessionError,
  } = useMutation({
    mutationKey: 'restoreSession',
    mutationFn: async () => await loadSession(),
    onError: error => {
      captureException(error);
      dispatch(authActions.login.error(error));
    },
  });

  const {
    mutate: login,
    isLoading: isLoadingLogin,
    isError: isLoginError,
    error: errorLogin,
    reset: resetLogin,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.login(),
    mutationFn: async (credentials: LoginArg) => {
      const response = await authClient.signIn.email({
        email: credentials.email,
        password: credentials.password,
      });

      // better-auth client doesn't throw on errors — it returns { data, error }
      // We need to throw explicitly so react-query routes to onError
      if (response.error) {
        throw response.error;
      }

      if (!response.data) {
        throw new Error('No data returned from login');
      }

      // In case 2FA is required for user and he has set it up already, redirect to 2FA verification page
      if ('twoFactorRedirect' in response.data && response.data.twoFactorRedirect) {
        history.push(
          { pathname: '/two-factor', search: location.search },
          { requireSetup: false, password: credentials.password },
        );
        clearIdentifiedUser();
        return;
      }

      // Sequentially load session, user, customer and redirect
      await loadSession(credentials.password, true);

      // Login is only truly complete once loadSession has run - the 2FA-redirect branch above returns earlier
      dispatch(authActions.login.success());
      clearIdentifiedUser();
    },
    onError: error => {
      // Error intentionally not dispatched, form is displaying error section
      console.error(error);
      captureException(error);
    },
  });

  const {
    mutate: loginToCustomer,
    isLoading: isLoadingLoginToCustomer,
    isError: isLoginToCustomerError,
    error: errorLoginToCustomer,
    reset: resetLoginToCustomer,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.loginToCustomer(),
    mutationFn: async (customerId: SerializedCustomer['_id']) => {
      await authClient.loginToCustomer(customerId);

      await loadSession(undefined, true);
    },
    onError: error => {
      console.error(error);
      captureException(error);
      dispatch(authActions.loginToCustomer.error(error));
    },
  });

  const {
    mutate: logoutFromCustomer,
    isLoading: isLoadingLogoutFromCustomer,
    isError: isLogoutFromCustomerError,
    error: errorLogoutFromCustomer,
    reset: resetLogoutFromCustomer,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.logoutFromCustomer(),
    mutationFn: async () => {
      await authClient.logoutFromCustomer();

      await loadSession();
    },
    onError: error => {
      console.error(error);
      captureException(error);
      dispatch(authActions.logoutFromCustomer.error(error));
    },
  });

  const {
    mutate: verifyTotp,
    mutateAsync: verifyTotpAsync,
    isLoading: isLoadingVerifyTotp,
    isError: isVerifyTotpError,
    error: errorVerifyTotp,
    reset: resetVerifyTotp,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.verifyTotp(),
    mutationFn: async (arg: Parameters<typeof authClient.twoFactor.verifyTotp>[0]) => {
      const { error } = await authClient.twoFactor.verifyTotp(arg);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      dispatch(authActions.twoFactor.verifyTotp.success());
    },
    onError: error => {
      console.error('Verify TOTP error:', error);
      captureException(error);
    },
  });

  const {
    mutateAsync: verifyBackupCodeAsync,
    isLoading: isLoadingVerifyBackupCode,
    isError: isVerifyBackupCodeError,
    error: errorVerifyBackupCode,
    reset: resetVerifyBackupCode,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.verifyBackupCode(),
    mutationFn: async (arg: Parameters<typeof authClient.twoFactor.verifyBackupCode>[0]) => {
      const response = await authClient.twoFactor.verifyBackupCode(arg);

      if (response.error) {
        throw response.error;
      }

      await loadSession();
    },
    onSuccess: () => {
      dispatch(authActions.twoFactor.verifyBackupCode.success());
    },
    onError: error => {
      console.error('Verify backup code error:', error);
      captureException(error);
    },
  });

  const {
    mutateAsync: enableTwoFactorAsync,
    isLoading: isLoadingEnableTwoFactor,
    isError: isEnableTwoFactorError,
    error: errorEnableTwoFactor,
    reset: resetEnableTwoFactor,
  } = useMutation<EnableTwoFactorData, unknown, EnableTwoFactorArg>({
    mutationKey: MUTATION_KEYS.auth.enableTwoFactor(),
    mutationFn: async arg => {
      const { data, error } = await authClient.twoFactor.enable(arg);

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from enable two-factor');
      }

      return data;
    },
    onError: error => {
      console.error('Enable two-factor error:', error);
      captureException(error);
    },
  });

  const {
    mutateAsync: disableTwoFactorAsync,
    isLoading: isLoadingDisableTwoFactor,
    isError: isDisableTwoFactorError,
    error: errorDisableTwoFactor,
    reset: resetDisableTwoFactor,
  } = useMutation<DisableTwoFactorData, unknown, DisableTwoFactorArg>({
    mutationKey: MUTATION_KEYS.auth.disableTwoFactor(),
    mutationFn: async arg => {
      const { data, error } = await authClient.twoFactor.disable(arg);

      if (error) {
        throw error;
      }

      await loadSession();

      return data;
    },
    onSuccess: () => {
      dispatch(authActions.twoFactor.disable.success());
    },
    onError: error => {
      console.error('Disable two-factor error:', error);
      captureException(error);
      dispatch(authActions.twoFactor.disable.error());
    },
  });

  const {
    mutate: logout,
    isLoading: isLoadingLogout,
    isError: isLogoutError,
    error: errorLogout,
    reset: resetLogout,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.logout(),
    mutationFn: async () => await authClient.signOut(),
    onSuccess: () => {
      dispatch(authActions.logout.success());
      clearIdentifiedUser();
      history.push('/');
    },
    onError: error => {
      console.error(error);
      captureException(error);
      dispatch(authActions.logout.error(error));
    },
  });

  const {
    mutate: resetPassword,
    isLoading: isLoadingResetPassword,
    isError: isResetPasswordError,
    error: errorResetPassword,
    reset: resetResetPassword,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.resetPassword(),
    mutationFn: async (payload: ResetPasswordArg) => {
      await authApiClient.resetPassword(payload);
    },
    onSuccess: () => {
      history.push('/');
      dispatch(authActions.resetPassword.success());
    },
    onError: error => {
      console.error(error);
      captureException(error);
      dispatch(authActions.resetPassword.error());
    },
  });

  const {
    mutate: setPassword,
    isLoading: isLoadingSetPassword,
    isError: isSetPasswordError,
    error: errorSetPassword,
    reset: resetSetPassword,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.setPassword(),
    mutationFn: async (payload: SetPasswordArg) => {
      await authApiClient.setPassword(payload);
    },
    onSuccess: () => {
      dispatch(authActions.setPassword.success());

      // Clear password reset token after successful password set
      clearPasswordResetToken();

      history.push('/');
    },
    onError: error => {
      // Error intentionally not dispatched, form is displaying error section
      console.error(error);
      captureException(error);
    },
  });

  const {
    mutateAsync: missingRecoveryCodeAsync,
    isLoading: isLoadingMissingRecoveryCode,
    isError: isMissingRecoveryCodeError,
    error: errorMissingRecoveryCode,
    reset: resetMissingRecoveryCode,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.missingRecoveryCode(),
    mutationFn: async (payload: MissingRecoveryCodeArg) => {
      await authApiClient.missingRecoveryCode(payload);
    },
    onError: error => {
      console.error(error);
      captureException(error);
    },
  });

  const {
    mutateAsync: approveOAuthConsentAsync,
    isLoading: isLoadingOAuthConsent,
    isError: isOAuthConsentError,
    error: errorOAuthConsent,
    reset: resetOAuthConsent,
  } = useMutation({
    mutationKey: MUTATION_KEYS.auth.oauthConsent(),
    mutationFn: async (oauthQuery: string) => await authApiClient.approveOAuthConsent({ oauthQuery }),
    onError: error => {
      console.error('OAuth consent error:', error);
      captureException(error);
    },
  });

  useEffect(() => {
    if (user) {
      if (location.pathname !== '/consent') redirectIfOAuthCodeFlow(location.search);
      return;
    }

    restoreSession();
  }, [user, restoreSession, location.search, location.pathname]);

  const globalIsLoading =
    isLoadingIdentify ||
    isLoadingGenerateOtp ||
    isLoadingVerifyOtp ||
    isLoadingLogin ||
    isLoadingLoginToCustomer ||
    isLoadingLogoutFromCustomer ||
    isLoadingLogout ||
    isLoadingResetPassword ||
    isLoadingSetPassword ||
    isLoadingMissingRecoveryCode ||
    isLoadingRestoreSession ||
    isLoadingVerifyTotp ||
    isLoadingVerifyBackupCode ||
    isLoadingEnableTwoFactor ||
    isLoadingDisableTwoFactor ||
    isLoadingOAuthConsent;

  // Authentication is complete when user is loaded
  const isAuthenticated = Boolean(user);

  const globalIsError =
    isIdentifyError ||
    isGenerateOtpError ||
    isVerifyOtpError ||
    isLoginError ||
    isLoginToCustomerError ||
    isLogoutFromCustomerError ||
    isLogoutError ||
    isResetPasswordError ||
    isSetPasswordError ||
    isMissingRecoveryCodeError ||
    isRestoreSessionError ||
    isVerifyTotpError ||
    isVerifyBackupCodeError ||
    isEnableTwoFactorError ||
    isDisableTwoFactorError ||
    isOAuthConsentError;

  const resetAllMutations = () => {
    resetIdentify();
    resetGenerateOtp();
    resetVerifyOtp();
    resetLogin();
    resetLoginToCustomer();
    resetLogoutFromCustomer();
    resetLogout();
    resetResetPassword();
    resetSetPassword();
    resetMissingRecoveryCode();
    resetVerifyTotp();
    resetVerifyBackupCode();
    resetEnableTwoFactor();
    resetDisableTwoFactor();
    resetOAuthConsent();
  };

  // Custom reset for identify that also clears sessionStorage
  const resetIdentifyWithStorage = () => {
    resetIdentify();
    clearIdentifiedUser();
    setIdentifiedUserVersion(v => v + 1);
  };

  return {
    identify,
    generateOtp,
    verifyOtp,
    login,
    loginToCustomer,
    logoutFromCustomer,
    logout,
    resetPassword,
    setPassword,
    missingRecoveryCodeAsync,
    verifyTotp,
    verifyTotpAsync,
    verifyBackupCodeAsync,
    enableTwoFactorAsync,
    disableTwoFactorAsync,
    approveOAuthConsentAsync,

    identifiedUser,

    loadSession,

    resetMutation: {
      all: resetAllMutations,
      identify: resetIdentifyWithStorage,
      generateOtp: resetGenerateOtp,
      verifyOtp: resetVerifyOtp,
      login: resetLogin,
      loginToCustomer: resetLoginToCustomer,
      logoutFromCustomer: resetLogoutFromCustomer,
      logout: resetLogout,
      resetPassword: resetResetPassword,
      setPassword: resetSetPassword,
      missingRecoveryCode: resetMissingRecoveryCode,
      verifyTotp: resetVerifyTotp,
      verifyBackupCode: resetVerifyBackupCode,
      enableTwoFactor: resetEnableTwoFactor,
      disableTwoFactor: resetDisableTwoFactor,
      oauthConsent: resetOAuthConsent,
    },

    isLoading: globalIsLoading,
    isAuthenticated,
    loading: {
      isLoadingIdentify,
      isLoadingGenerateOtp,
      isLoadingVerifyOtp,
      isLoadingLogin,
      isLoadingLoginToCustomer,
      isLoadingLogoutFromCustomer,
      isLoadingLogout,
      isLoadingResetPassword,
      isLoadingSetPassword,
      isLoadingMissingRecoveryCode,
      isLoadingRestoreSession,
      isLoadingVerifyTotp,
      isLoadingVerifyBackupCode,
      isLoadingEnableTwoFactor,
      isLoadingDisableTwoFactor,
      isLoadingOAuthConsent,
    },

    isError: globalIsError,
    error: {
      identify: errorIdentify,
      generateOtp: errorGenerateOtp,
      verifyOtp: errorVerifyOtp,
      login: errorLogin,
      loginToCustomer: errorLoginToCustomer,
      logoutFromCustomer: errorLogoutFromCustomer,
      logout: errorLogout,
      resetPassword: errorResetPassword,
      setPassword: errorSetPassword,
      missingRecoveryCode: errorMissingRecoveryCode,
      restoreSession: errorRestoreSession,
      verifyTotp: errorVerifyTotp,
      verifyBackupCode: errorVerifyBackupCode,
      enableTwoFactor: errorEnableTwoFactor,
      disableTwoFactor: errorDisableTwoFactor,
      oauthConsent: errorOAuthConsent,
    },
  };
};
